import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getJob, downloadUrl, syncToHubspot, cancelJob } from '../api';
import StatusBadge from '../components/StatusBadge';
import ProgressBar from '../components/ProgressBar';
import LiveLog from '../components/LiveLog';
import ResultStats from '../components/ResultStats';
import '../styles/components/badges.css';
import '../styles/components/buttons.css';
import '../styles/components/job-info.css';
import '../styles/components/table.css';

const ENGINE_LABELS = {
  apify: 'Apify',
  zenrows: 'ZenRows',
  scrapingdog: 'ScrapingDog',
};

const DISCOVERY_DEFAULTS = {
  1: { output_validation: false },
  2: { output_validation: true },
};

const AGE_DEFAULTS = {
  1: { skip_linkedin: true,  skip_website: false },
  2: { skip_linkedin: false, skip_website: false },
};

const FLAG_LABELS = {
  skip_linkedin: { on: 'Skip LinkedIn', off: 'LinkedIn ON' },
  skip_website: { on: 'Skip Website', off: 'Website ON' },
  output_validation: { on: 'Output Validation', off: 'No Validation' },
};

function jobBadges(job) {
  const badges = [];
  const flags = job.flags || {};
  const level = job.level || 1;

  badges.push({ label: job.mode === 'discovery' ? 'Discovery' : 'Age Estimation', cls: 'badge-mode' });
  badges.push({ label: `Level ${level}`, cls: 'badge-level' });

  if (flags.start || flags.limit) {
    const parts = [];
    if (flags.start) parts.push(`#${flags.start}`);
    if (flags.limit) parts.push(`${flags.limit} rows`);
    badges.push({ label: parts.join(', '), cls: 'badge-range' });
  }

  const needsLinkedIn = job.mode === 'discovery'
    ? !!flags.output_validation
    : !flags.skip_linkedin;
  if (needsLinkedIn) {
    badges.push({ label: ENGINE_LABELS[flags.linkedin_engine] || 'Apify', cls: 'badge-engine' });
  }

  // Show flags that override level defaults
  const defaults = job.mode === 'discovery' ? DISCOVERY_DEFAULTS[level] : AGE_DEFAULTS[level];
  if (defaults) {
    for (const [key, defaultVal] of Object.entries(defaults)) {
      if (flags[key] != null && !!flags[key] !== !!defaultVal) {
        const info = FLAG_LABELS[key];
        if (info) {
          badges.push({ label: flags[key] ? info.on : info.off, cls: 'badge-override' });
        }
      }
    }
  }

  if (flags.fresh) {
    badges.push({ label: 'Fresh', cls: 'badge-override' });
  }

  return badges;
}

export default function JobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [disconnected, setDisconnected] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncRetry, setSyncRetry] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const cancellingRef = useRef(false);
  const cancelStartRef = useRef(0);
  const pollRef = useRef(null);

  useEffect(() => {
    setJob(null);
    setError(null);
    setDisconnected(false);
    setCurrentRow(null);

    let cancelled = false;
    let hadJob = false;

    const poll = async () => {
      try {
        const j = await getJob(id);
        if (cancelled) return;
        hadJob = true;
        setJob(j);
        setDisconnected(false);
        if (j.status === 'completed' || j.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 404 && cancellingRef.current) {
          clearInterval(pollRef.current);
          const elapsed = Date.now() - cancelStartRef.current;
          const wait = Math.max(0, 2000 - elapsed);
          setTimeout(() => navigate('/jobs'), wait);
          return;
        }
        if (hadJob) {
          setDisconnected(true);
        } else {
          setError(err.response?.data?.detail || 'Failed to load job');
          clearInterval(pollRef.current);
        }
      }
    };

    poll();
    pollRef.current = setInterval(poll, 2000);

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [id]);

  if (error) {
    return (
      <div className="page-enter">
        <p style={{ color: 'var(--red)' }}>{error}</p>
        <Link to="/jobs" style={{ color: 'var(--accent)', fontSize: 14 }}>Back to Job History</Link>
      </div>
    );
  }

  if (!job) {
    return <div className="page-enter" style={{ color: 'var(--text3)' }}>Loading...</div>;
  }

  const running = job.status === 'running' || job.status === 'pending';

  return (
    <div className="page-enter">
      {cancelling && (
        <div className="cancel-overlay">
          <div className="cancel-overlay-content">
            <div className="cancel-spinner" />
            <div className="cancel-overlay-text">Cancelling, deleting this job's data...</div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="job-header">
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 6, letterSpacing: '-0.6px' }}>
            {job.filename}
          </h2>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 2 }}>
            <StatusBadge status={job.status} />
            {jobBadges(job).map(b => (
              <span key={b.cls} className={`info-badge ${b.cls}`}>{b.label}</span>
            ))}
            {job.created_at && (
              <span className="info-badge badge-time">
                {new Date(job.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}{' '}
                {new Date(job.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        </div>
        {job.status === 'completed' && (
          <div className="job-header-actions">
            <button
              className="btn btn-hubspot btn-header"
              onClick={() => setSyncOpen(v => !v)}
            >
              Sync to HubSpot
            </button>
            <a href={downloadUrl(id)} className="btn btn-primary btn-header" style={{ textDecoration: 'none' }}>
              Download Result
            </a>
          </div>
        )}
        {(job.status === 'pending' || job.status === 'running' || job.status === 'cancelling') && (() => {
          const prog = job.progress || {};
          const nearEnd = job.status === 'running' && prog.total > 0 && prog.processed >= prog.total - 2;
          if (nearEnd && !cancelling) return null;
          return (
            <div className="job-header-actions">
              <button
                className="btn btn-danger btn-header"
                disabled={cancelling}
                onClick={async () => {
                  if (!window.confirm('Cancel this job? It will be deleted.')) return;
                  setCancelling(true);
                  cancellingRef.current = true;
                  cancelStartRef.current = Date.now();
                  try {
                    await cancelJob(id);
                    if (job.status === 'pending') {
                      const elapsed = Date.now() - cancelStartRef.current;
                      const wait = Math.max(0, 2000 - elapsed);
                      setTimeout(() => navigate('/jobs'), wait);
                      return;
                    }
                    // Running jobs: worker handles cleanup, polling will detect 404
                  } catch (err) {
                    setError(err.response?.data?.detail || 'Cancel failed');
                    setCancelling(false);
                    cancellingRef.current = false;
                  }
                }}
              >
                {cancelling ? 'Cancelling...' : 'Cancel'}
              </button>
            </div>
          );
        })()}
      </div>

      {syncOpen && (
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '16px 20px',
          marginBottom: 16,
          boxShadow: 'var(--shadow-card)',
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
            Are you sure you want to sync to HubSpot?
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text2)', marginBottom: 14, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={syncRetry}
              onChange={e => setSyncRetry(e.target.checked)}
            />
            Retry previously failed rows
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-primary"
              disabled={syncing}
              onClick={async () => {
                setSyncing(true);
                try {
                  const newJob = await syncToHubspot(id, syncRetry);
                  navigate(`/jobs/${newJob.id}`);
                } catch (err) {
                  setError(err.response?.data?.detail || 'Sync failed');
                } finally {
                  setSyncing(false);
                  setSyncOpen(false);
                }
              }}
            >
              {syncing ? 'Creating...' : 'Confirm'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => { setSyncOpen(false); setSyncRetry(false); }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {disconnected && (
        <div style={{
          background: 'rgba(234, 179, 8, 0.08)',
          border: '1px solid rgba(234, 179, 8, 0.25)',
          borderRadius: 'var(--radius)',
          padding: '12px 20px',
          marginBottom: 16,
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--yellow)',
        }}>
          Connection lost — retrying. Job will resume automatically when the server is back.
        </div>
      )}

      {job.status === 'failed' && (
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '20px 24px',
          marginBottom: 16,
          boxShadow: 'var(--shadow-card)',
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--red)', marginBottom: 6 }}>Failed</div>
          <p style={{ fontSize: 14, color: 'var(--text2)', margin: 0 }}>{job.error || 'Unknown error'}</p>
        </div>
      )}

      {/* Result stats cards */}
      <ResultStats stats={job.job_summary || {}} mode={job.flags?.discovery ? 'discovery' : 'age'} />

      {/* Progress bar */}
      <ProgressBar job={job} currentRow={running ? currentRow : null} />

      {/* Logs */}
      <LiveLog jobId={id} active={running} onCurrentRow={setCurrentRow} />

      {/* Run history */}
      {job.runs && job.runs.length > 1 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10, color: 'var(--text2)' }}>
            {job.mode === 'discovery' ? 'Discovery' : 'Age Estimation'} runs for this file
          </div>
          <div className="jobs-table-wrap">
            <table className="jobs-table">
              <thead>
                <tr>
                  <th className="col-status">Status</th>
                  <th className="col-level">Lvl</th>
                  <th className="col-rows">Rows</th>
                  <th className="col-range">Range</th>
                  <th className="col-duration">Duration</th>
                  <th className="col-created">Date</th>
                </tr>
              </thead>
              <tbody>
                {job.runs.map(run => {
                  const isCurrent = run.id === id;
                  const range = [];
                  if (run.start) range.push(`#${run.start}`);
                  if (run.limit) range.push(`${run.limit} limit`);
                  const dur = run.started_at && run.completed_at
                    ? (() => {
                        const s = Math.floor((new Date(run.completed_at) - new Date(run.started_at)) / 1000);
                        if (s < 60) return `${s}s`;
                        const m = Math.floor(s / 60);
                        const h = Math.floor(m / 60);
                        return h > 0 ? `${h}h ${m % 60}m` : `${m}m ${s % 60}s`;
                      })()
                    : '-';
                  return (
                    <tr
                      key={run.id}
                      onClick={() => !isCurrent && navigate(`/jobs/${run.id}`)}
                      style={isCurrent ? { background: 'var(--surface2)', boxShadow: 'inset 3px 0 0 var(--accent)' } : run.superseded ? { opacity: 0.45 } : undefined}
                    >
                      <td className="col-status">
                        <StatusBadge status={run.status} />
                      </td>
                      <td className="col-level">{run.level || '-'}</td>
                      <td className="col-rows">{run.total_rows || '-'}</td>
                      <td className="col-range">{range.length > 0 ? range.join(', ') : 'All'}</td>
                      <td className="col-duration">{dur}</td>
                      <td className="col-created">
                        {run.started_at
                          ? <>
                              {new Date(run.started_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}{' '}
                              {new Date(run.started_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                            </>
                          : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
